import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { apiService } from '../../services/apiService';
import type { ChangesetStatus, KeywordChangeset } from '../../types/keywords';
import { ConfirmDialog } from './ConfirmDialog';
import { describeError, formatDateTime } from './utils';

const POLL_INTERVAL_MS = 10_000;

const STATUS_COLORS: Record<ChangesetStatus, 'default' | 'info' | 'success' | 'error'> = {
  requested: 'default',
  applying: 'info',
  applied: 'success',
  failed: 'error',
};

function isOpen(changeset: KeywordChangeset): boolean {
  return changeset.status !== 'applied';
}

function ChangeSummary({ changeset }: { changeset: KeywordChangeset }) {
  const { t } = useTranslation('admin');
  const { diff, options } = changeset;
  const parts = [
    diff.added.length && `+${diff.added.length}`,
    diff.removed.length && `−${diff.removed.length}`,
    diff.merged.length && t('history.merged', { count: diff.merged.length }),
    diff.relabeled.length && t('history.relabeled', { count: diff.relabeled.length }),
    diff.reevaluated.length && t('history.reevaluated', { count: diff.reevaluated.length }),
  ].filter(Boolean);
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <span>{parts.join(' · ')}</span>
      {options.full_retag && <Chip size="small" variant="outlined" label={t('history.fullRetag')} />}
    </Stack>
  );
}

export function HistoryTab() {
  const { t, i18n } = useTranslation('admin');
  const [changesets, setChangesets] = useState<KeywordChangeset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retagCalls, setRetagCalls] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setChangesets(await apiService.getKeywordChangesets());
    } catch (err) {
      setError(describeError(err, t));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh while a commit is waiting or being applied.
  const polling = changesets?.some((c) => c.status === 'requested' || c.status === 'applying') ?? false;
  useEffect(() => {
    if (!polling) return;
    const timer = window.setInterval(load, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [polling, load]);

  const openRetag = async () => {
    setConfirming(true);
    setRetagCalls(null);
    try {
      setRetagCalls((await apiService.reviewKeywordDraft()).effort.full_retag_llm_calls);
    } catch {
      setRetagCalls(null); // the dialog still works without the estimate
    }
  };

  const retag = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiService.forceKeywordRetag();
      await load();
    } catch (err) {
      setError(describeError(err, t));
    } finally {
      setConfirming(false);
      setBusy(false);
    }
  };

  if (!changesets) return error ? <Alert severity="error">{error}</Alert> : <CircularProgress />;

  return (
    <Box>
      <Stack direction="row" sx={{ mb: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={openRetag}
          disabled={changesets.length === 0 || changesets.some(isOpen)}
        >
          {t('history.forceRetag')}
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('history.revision')}</TableCell>
            <TableCell>{t('history.status')}</TableCell>
            <TableCell>{t('history.requested')}</TableCell>
            <TableCell>{t('history.changes')}</TableCell>
            <TableCell>{t('history.progress')}</TableCell>
            <TableCell align="right">{t('history.llmCalls')}</TableCell>
            <TableCell align="right">{t('history.retagged')}</TableCell>
            <TableCell align="right">{t('history.changedUnrelated')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changesets.map((changeset) => {
            const { progress, stats } = changeset;
            return (
              <TableRow key={changeset.revision}>
                <TableCell>{changeset.revision}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLORS[changeset.status]}
                    label={t(`history.statuses.${changeset.status}`)}
                  />
                  {changeset.error && (
                    <Typography variant="caption" color="error" component="div">{changeset.error}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDateTime(changeset.requested_at, i18n.language)}</Typography>
                  <Typography variant="caption" color="text.secondary">{changeset.requested_by}</Typography>
                </TableCell>
                <TableCell><ChangeSummary changeset={changeset} /></TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  {changeset.status === 'applying' && progress.total > 0 && (
                    <>
                      <Typography variant="caption">
                        {progress.phase} {progress.done}/{progress.total}
                      </Typography>
                      <LinearProgress variant="determinate" value={(100 * progress.done) / progress.total} />
                    </>
                  )}
                  {changeset.status === 'applying' && progress.total === 0 && <LinearProgress />}
                </TableCell>
                <TableCell align="right">{stats.llm_calls}</TableCell>
                <TableCell align="right">{stats.docs_retagged}</TableCell>
                <TableCell align="right">{stats.docs_changed_unrelated}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {changesets.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{t('history.empty')}</Typography>
      )}

      <ConfirmDialog
        open={confirming}
        title={t('history.forceRetagTitle')}
        confirmLabel={t('history.forceRetag')}
        busy={busy}
        onConfirm={retag}
        onClose={() => setConfirming(false)}
      >
        {retagCalls === null
          ? t('history.forceRetagText')
          : t('history.forceRetagTextWithCalls', { formatted: retagCalls.toLocaleString(i18n.language) })}
      </ConfirmDialog>
    </Box>
  );
}
